import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@/lib/auth'
import { initials } from '@/lib/format'
import { Button, cx } from './ui/primitives'

/**
 * Who you are signed in as, and the way out.
 *
 * The staff application's `UserMenu` with one substitution: a partner has no roles, so the
 * line that names them names their company instead. That is the same fact in both places -
 * what this account is entitled to act as - and for a partner it is the whole of it, since
 * their entire reach is one foreign key.
 *
 * Sign out moved inside it. Standing beside the name in the header, it was a bare word
 * competing with five navigation items for the same glance, and the header now has a
 * wordmark and a centred nav to fit as well. Behind the name it is where somebody already
 * looks for it, and it stops being a one-press action sitting next to nothing.
 *
 * Its own copy rather than an import, like the rest of `components/`: the two applications
 * are built and deployed separately and neither may hold up the other's release.
 */
export function PartnerMenu() {
  const { partner, signOut } = useAuth()
  const [open, setOpen] = useState(false)
  const container = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return

    function onPointerDown(event: PointerEvent) {
      if (!container.current?.contains(event.target as Node)) setOpen(false)
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  if (!partner) return null

  return (
    <div ref={container} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Signed in as ${partner.displayName}`}
        onClick={() => setOpen((was) => !was)}
        className={cx(
          'flex items-center gap-2 rounded-full py-1 pl-1 pr-2 transition-colors hover:bg-sunken',
          open && 'bg-sunken',
        )}
      >
        <span
          aria-hidden
          className="brand-gradient grid size-7 shrink-0 place-items-center rounded-full font-mono text-[11px] font-semibold text-white"
        >
          {initials(partner.displayName)}
        </span>
        <span className="hidden max-w-40 truncate text-[13px] font-medium sm:block">
          {partner.displayName}
        </span>
        <svg aria-hidden viewBox="0 0 12 12" className="size-3 text-muted">
          <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 w-64 rounded-lg border border-line bg-surface p-1 shadow-float"
        >
          <div className="border-b border-line-soft px-3 py-2.5">
            <p className="truncate text-[13px] font-medium">{partner.displayName}</p>
            <p className="truncate text-[12px] text-muted">{partner.email}</p>
            <p className="mt-1 truncate font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
              {partner.resellerName}
            </p>
          </div>

          <Button
            role="menuitem"
            variant="ghost"
            size="sm"
            className="mt-1 w-full justify-start"
            onClick={() => {
              setOpen(false)
              void signOut()
            }}
          >
            Sign out
          </Button>
        </div>
      )}
    </div>
  )
}
